import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircleIcon, SparkleIcon } from "@phosphor-icons/react";

function AiKeyCard() {
  const [hasKey, setHasKey] = useState(false);
  const [orgKeyConfigured, setOrgKeyConfigured] = useState(false);
  const [checking, setChecking] = useState(true);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/account/ai-key");
      setHasKey(!!data.has_key);
      setOrgKeyConfigured(!!data.org_key_configured);
    } catch (e) { /* leave state as-is; the save action will surface any real problem */ }
    finally { setChecking(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!value.trim()) { toast.error("Enter a key first"); return; }
    setSaving(true);
    try {
      await api.post("/account/ai-key", { api_key: value.trim() });
      setValue("");
      setHasKey(true);
      toast.success("API key saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail || e)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirm("Remove your saved Anthropic API key? Ask AI will stop working until you add a new one.")) return;
    setSaving(true);
    try {
      await api.delete("/account/ai-key");
      setHasKey(false);
      toast.success("API key removed");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail || e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-md p-6 bg-white">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-md bg-[#EFF6FF] flex items-center justify-center">
          <SparkleIcon size={20} weight="fill" color="#0176D3" />
        </div>
        <div>
          <div className="font-medium text-[#0F172A]">Ask AI — Anthropic API key</div>
          <div className="text-xs text-[#475569]">
            {orgKeyConfigured
              ? "Optional — an organization key is already set up, so Ask AI works without this."
              : "Personal key for Ask AI — most employees don't have Anthropic Console access to create one; ask your admin to set an org-wide key instead if you'd rather not manage your own."}
          </div>
        </div>
      </div>

      {!checking && (
        <div className={`text-sm rounded-md p-3 mb-4 flex items-center gap-2 ${hasKey || orgKeyConfigured ? "text-[#059669] bg-[#DCFCE7] border border-[#86EFAC]" : "text-zinc-600 bg-zinc-50 border border-border"}`} data-testid="ai-key-status">
          {(hasKey || orgKeyConfigured) ? <CheckCircleIcon size={16} weight="fill" /> : null}
          {hasKey
            ? "Your personal key is configured and will be used for your chats."
            : orgKeyConfigured
              ? "No personal key set — using the organization's key for Ask AI."
              : "No key configured yet — Ask AI won't work until an org key or a personal key is added."}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <Label className="trk-uppercase-label mb-2 block">{hasKey ? "Replace key" : "Personal API key"}</Label>
          <PasswordInput
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="sk-ant-..."
            className="h-11 rounded-md font-mono text-sm"
            data-testid="ai-key-input"
            autoComplete="off"
          />
          <div className="text-xs text-zinc-500 mt-1.5">
            Only needed if you want to use your own Anthropic billing instead of the org key. Get a key from console.anthropic.com → Settings → API Keys. Stored securely — never shown again after saving.
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving || !value.trim()} data-testid="save-ai-key" className="rounded-md bg-[#0176D3] hover:bg-[#005FB2] text-white font-medium">
            {saving ? "Saving…" : "Save key"}
          </Button>
          {hasKey && (
            <Button onClick={remove} disabled={saving} variant="outline" data-testid="remove-ai-key" className="rounded-md text-red-700 border-red-200 hover:bg-red-50">
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// No password section: sign-in is Microsoft SSO only (see AuthContext.jsx),
// so a password is managed in Microsoft 365, not here.
export default function AccountPage() {
  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">Account</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0F172A]">Account settings</h1>
        </div>
      </div>

      <div className="px-8 py-8 max-w-xl trk-fade-in">
        <AiKeyCard />
      </div>
    </AppLayout>
  );
}
